<template>
	<div v-if="emailTemplateType">
		<bread-crumbs
			:bread-crumbs="breadCrumbs"
			:current-title="emailTemplateType.name"
			class="breadcrumbstyle"
		/>
		<v-container
			fluid
		>
			<v-row>
				<v-col
					cols="4"
				>
					<v-row>
						<v-col>
							<v-card>
								<v-card-title>
									<translate>Details</translate>
								</v-card-title>
								<v-card-text>
									<div
										v-for="parameter in Object.keys(emailTemplateType)"
										:key="parameter"
									>
										<v-row>
											<v-col>
												<translate class="text-primary">
													{{ formatFieldString(parameter) }}
												</translate>
												<purpose-select
													v-if="parameter === 'purpose' && purposes"
													v-model="emailTemplateType[parameter]"
													style="font-size: 100%"
													clearable
													variant="outlined"
													hide-details
												/>
												<v-text-field
													v-else
													v-model="emailTemplateType[parameter]"
													style="font-size: 100%"
													variant="outlined"
													:disabled="!isEditable(parameter, emailTemplateType)"
													hide-details
												/>
											</v-col>
										</v-row>
									</div>
								</v-card-text>
								<v-card-actions>
									<v-spacer />
									<confirm-button
										v-if="emailTemplateType.method === 'manual'"
										color="error"
										:text="$gettext('Are you sure you want to delete this template?')"
										:callback="() => { deleteTemplateType() }"
									>
										<translate>
											Delete
										</translate>
									</confirm-button>
									<v-btn
										color="primary"
										variant="text"
										@click="updateEmailTemplateType"
									>
										<translate>Save</translate>
									</v-btn>
								</v-card-actions>
							</v-card>
						</v-col>
					</v-row>
					<v-row>
						<v-col>
							<v-card>
								<v-card-title>
									<translate>Comments</translate>
								</v-card-title>
								<v-card-text>
									<v-row>
										<v-col>
											<translate>
												Explanation Business Rules
											</translate>
										</v-col>
									</v-row>
									<v-row no-gutters>
										<v-col>
											<v-textarea
												v-model="businessRules"
												no-resize
												variant="outlined"
												rows="4"
											/>
										</v-col>
									</v-row>
									<v-row no-gutters>
										<v-spacer />
										<v-col
											cols="auto"
										>
											<v-btn
												color="primary"
												variant="text"
												@click="updateEmailTemplateBusinessRules"
											>
												<translate>Save</translate>
											</v-btn>
										</v-col>
									</v-row>
									<v-row>
										<v-col>
											<translate>
												History
											</translate>
										</v-col>
									</v-row>
									<v-row
										v-for="historyNote in historyNotes"
										:key="historyNote"
									>
										<v-col>
											<b>{{ historyNote.createdAt + ' ' + historyNote.writer }}</b><br>
											{{ historyNote.note }}
										</v-col>
									</v-row>
									<v-row no-gutters>
										<v-col>
											<v-textarea
												v-model="newNote"
												no-resize
												variant="outlined"
												rows="2"
											/>
										</v-col>
									</v-row>
								</v-card-text>
								<v-card-actions>
									<v-spacer />
									<v-btn
										color="primary"
										@click="addHistoryNote"
									>
										<translate>Add</translate>
									</v-btn>
								</v-card-actions>
							</v-card>
						</v-col>
					</v-row>
				</v-col>
				<v-col
					cols="8"
				>
					<email-template-overview
						:email-template-type="emailTemplateType"
					/>
				</v-col>
			</v-row>
		</v-container>
	</div>
</template>

<script>
import {
	axios,
} from '../../../../services/axios-service';
import {
	displayError,
} from 'laravel-front-library';
import PurposeSelect from '../../../molecules/PurposeSelect.vue';
import ConfirmButton from '../../../atoms/ConfirmButton.vue';
import EmailTemplateOverview from '../../../organisms/EmailTemplateOverview.vue';

export default {
	name: 'EmailTemplateTypeView',
	components: {
		EmailTemplateOverview,
		ConfirmButton,
		PurposeSelect
	},
	data () {
		return {
			breadCrumbs: {
				'/bo/emailtemplates': 'Email Templates'
			},
			title: 'Enter email template type here',
			items: [],
			purposes: null,
			emailTemplateType: null,
			emailTemplateTypeId: null,
			businessRules: '',
			historyNotes: [],
			newNote: '',
			editable: [
				{
					name: 'title',
					auto: true,
				},
				{
					name: 'variables',
					auto: false,
				},
				{
					name: 'purpose',
					auto: true,
				},
			]
		};
	},

	created () {
		const uri = window.location.search.substring(1);
		const params = new URLSearchParams(uri);

		if (params.get('id')) {
			this.emailTemplateTypeId = parseInt(params.get('id'));
			this.getViewData();
		}
	},

	methods: {

		/**
		 * Send request to delete template
		 */
		deleteTemplateType () {
			axios.delete(`/bo/emailtemplatetypes?id=${this.emailTemplateTypeId}`).then(() => {
				window.location.href = '/bo/emailtemplates';
			});
		},

		/**
		 * Get email template type, email template, and available purpose data for the page.
		 *
		 * @returns {void}
		 */
		getViewData () {
			axios.get(`/bo/emailtemplates/viewdata?id=${this.emailTemplateTypeId}`)
				.then(({
					data: data
				}) => {
					this.emailTemplateType = data.emailTemplateType;
					this.purposes = data.purposes;
					this.businessRules = data.businessRules;
					this.historyNotes = data.historyNotes;
				})
				.catch(() => {
					displayError(this.$gettext('Something has gone wrong during data retrieval'));
				});
		},

		/**
		 * Indicates whether a given parameter is editable on a given template
		 *
		 * @param {object} parameter
		 * @param {object} template
		 * @returns {boolean}
		 */
		isEditable (parameter, template) {
			return this.editable.some((e) => (e.name === parameter) && ((template.method !== 'auto') || e.auto));
		},

		/**
		 * Save the changes to the email template type
		 *
		 * @returns {void}
		 */
		updateEmailTemplateType () {
			const formData = new FormData();

			formData.append('title', this.emailTemplateType.title);
			formData.append('variables', this.emailTemplateType.variables);
			formData.append('emailtemplatetypepurpose_id', this.emailTemplateType.purpose ? this.emailTemplateType.purpose : '');
			formData.append('id', this.emailTemplateTypeId);

			axios({
				url: '/bo/emailtemplatetypes',
				method: 'POST',
				dataType: 'application/json',
				data: formData,
				config: {
					headers: {
						'Content-Type': 'multipart/form-data'
					},
				},
			}).catch((error) => {
				displayError(error);
			}).finally(() => {
				window.location.reload();
			});
		},

		/**
		 * Save the changes to the email template type business rules
		 *
		 * @returns {void}
		 */
		updateEmailTemplateBusinessRules () {
			const formData = new FormData();

			formData.append('id', this.emailTemplateTypeId);
			formData.append('business_rules', this.businessRules);

			axios({
				url: 'businessrules',
				method: 'POST',
				dataType: 'application/json',
				data: formData,
				config: {
					headers: {
						'Content-Type': 'multipart/form-data'
					},
				},
			}).catch((error) => {
				displayError(error);
			}).finally(() => {
				window.location.reload();
			});
		},

		/**
		 * Add a new history note to the email template type
		 *
		 * @returns {void}
		 */
		addHistoryNote () {
			const formData = new FormData();

			formData.append('id', this.emailTemplateTypeId);
			formData.append('newNote', this.newNote);

			axios({
				url: 'historynote',
				method: 'POST',
				dataType: 'application/json',
				data: formData,
				config: {
					headers: {
						'Content-Type': 'multipart/form-data'
					},
				},
			}).catch((error) => {
				displayError(error);
			}).finally(() => {
				window.location.reload();
			});
		},

		/**
		 * formats field string to be used as a field header on the page
		 *
		 * @param {string} string - Field string to be formatted
		 * @returns {string} - Formatted string to be used as field header
		 */
		formatFieldString (string) {
			const headerString = string.replace(/([a-zA-Z])([A-Z])([a-z])/g, '$1 $2$3');

			return headerString.charAt(0).toUpperCase() + headerString.slice(1).toLowerCase();
		},

	},
};
</script>

<style scoped>

</style>
